import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Alert,
  Link,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import axios from '../../api/axios';

const FILE_TYPE_CONFIG = {
  pdf: {
    accept: '.pdf',
    mimes: ['application/pdf'],
    label: 'PDF',
  },
  doc: {
    accept: '.doc',
    mimes: ['application/msword'],
    label: 'DOC',
  },
  docx: {
    accept: '.docx',
    mimes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    label: 'DOCX',
  },
  xls: {
    accept: '.xls',
    mimes: ['application/vnd.ms-excel'],
    label: 'XLS',
  },
  xlsx: {
    accept: '.xlsx',
    mimes: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    label: 'XLSX',
  },
  jpg: {
    accept: '.jpg,.jpeg',
    mimes: ['image/jpeg'],
    label: 'JPG',
  },
  png: {
    accept: '.png',
    mimes: ['image/png'],
    label: 'PNG',
  },
  other: {
    accept: '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png',
    mimes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/png',
    ],
    label: 'документ',
  },
};

const FileUploader = ({ onUploadSuccess, currentFile, fileType = 'pdf', maxSizeMB = 100, inputId = 'file-upload-input' }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const config = FILE_TYPE_CONFIG[fileType] || FILE_TYPE_CONFIG.other;

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!config.mimes.includes(file.type)) {
      setError(`Пожалуйста, выберите файл типа ${config.label}`);
      return;
    }

    // Проверка размера файла
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`Размер файла не должен превышать ${maxSizeMB}MB`);
      return;
    }

    setError('');
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        const apiUrl = process.env.REACT_APP_API_URL || '/api';
        let baseUrl;
        if (apiUrl.startsWith('http://') || apiUrl.startsWith('https://')) {
          baseUrl = apiUrl.replace('/api', '');
        } else {
          baseUrl = window.location.origin;
        }
        const fileUrl = `${baseUrl}${response.data.url}`;
        onUploadSuccess(fileUrl);
      }
    } catch (error) {
      console.error('Ошибка загрузки файла:', error);
      setError(error.response?.data?.error || 'Ошибка при загрузке файла');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ my: 2 }}>
      {currentFile && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Текущий файл:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon color="primary" />
            <Link
              href={currentFile}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: 'none' }}
            >
              <Typography variant="body2" color="primary">
                Открыть файл
              </Typography>
            </Link>
          </Box>
        </Box>
      )}

      <input
        accept={config.accept}
        style={{ display: 'none' }}
        id={inputId}
        type="file"
        onChange={handleFileChange}
        disabled={uploading}
      />
      <label htmlFor={inputId}>
        <Button
          variant="outlined"
          component="span"
          startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          disabled={uploading}
        >
          {uploading ? 'Загрузка...' : `Загрузить ${config.label} файл`}
        </Button>
      </label>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default FileUploader;
